/* eslint-disable react/jsx-no-bind */
/**
 * #######################################################@
 *
 * Compact multi-select filter, made for <FiltersPanel />
 *
 * Modern, professional replacement of <ButtonMenu /> for the
 * "code" filter type: a select-like trigger (full width, shows
 * the current selection) that opens a popover with a search
 * bar and a compact checkbox list.
 *
 * ButtonMenu stays untouched (still used for the boolean type).
 *
 * #######################################################@
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Divider, Icon, InputAdornment, Popover } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDInput from "components/Basics/MDInput";
import MDTypography from "components/Basics/MDTypography";
import FilterTrigger from "components/Custom/Filters/FilterTrigger";
import { useMaterialUIController } from "context";
import i18n from "i18n";

export default function CompactSelectFilter({ items = [], storage, handleUpdate }) {
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;

	const [selectedItems, setSelectedItems] = useState(storage || []);
	const [anchorEl, setAnchorEl] = useState(null);
	const [anchorWidth, setAnchorWidth] = useState(0);
	const [search, setSearch] = useState("");

	const open = Boolean(anchorEl);

	// Propagate selection changes to the store, but skip the very
	// first render (the store already reflects `storage`).
	const isFirstRender = useRef(true);
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		handleUpdate(selectedItems);
	}, [selectedItems]);

	const isSelected = item =>
		Boolean(selectedItems.find(selected => selected?.value === item?.value));

	const toggleItem = item => {
		if (isSelected(item)) {
			setSelectedItems(selectedItems.filter(selected => selected?.value !== item?.value));
		} else {
			setSelectedItems([...selectedItems, item]);
		}
	};

	const toggleAll = () => {
		setSelectedItems(selectedItems.length === items.length ? [] : [...items]);
	};

	const openPopover = e => {
		setAnchorWidth(e.currentTarget.offsetWidth);
		setAnchorEl(e.currentTarget);
	};

	const closePopover = () => {
		setAnchorEl(null);
		setSearch("");
	};

	const filteredItems = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return items;
		return items.filter(item => String(item?.label ?? "").toLowerCase().includes(term));
	}, [items, search]);

	const summary = selectedItems.map(item => item?.label).join(", ");
	const hasSelection = selectedItems.length > 0;

	return (
		<>
			<FilterTrigger
				open={open}
				active={hasSelection}
				text={summary}
				placeholder={i18n.t("FILTERS.panel.selectPlaceholder")}
				badge={hasSelection ? selectedItems.length : null}
				onClick={openPopover}
				darkMode={darkMode}
			/>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={closePopover}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				slotProps={{
					paper: {
						sx: ({ palette, borders, boxShadows }) => ({
							mt: 0.5,
							width: anchorWidth || 240,
							borderRadius: borders.borderRadius.lg,
							border: `1px solid ${darkMode ? palette.grey[700] : palette.grey[300]}`,
							backgroundColor: darkMode ? palette.background.card : palette.white.main,
							boxShadow: boxShadows.lg,
							overflow: "hidden"
						})
					}
				}}
			>
				{/* Search bar */}
				<MDBox p={1} pb={0.5}>
					<MDInput
						fullWidth
						size="small"
						autoFocus
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder={i18n.t("FORMS.PLACEHOLDERS.search")}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Icon fontSize="small" sx={{ color: "text.secondary" }}>
										search
									</Icon>
								</InputAdornment>
							)
						}}
					/>
				</MDBox>

				{/* Select / unselect all */}
				<MDBox px={1}>
					<MDButton
						variant="text"
						color="info"
						size="small"
						fullWidth
						onClick={toggleAll}
						sx={{ justifyContent: "flex-start", textTransform: "none", py: 0.25 }}
					>
						<Icon fontSize="small">
							{selectedItems.length === items.length && items.length > 0
								? "remove_done"
								: "done_all"}
						</Icon>
						&nbsp;{i18n.t("FILTERS.menu.selectUnselect")}
					</MDButton>
				</MDBox>

				<Divider sx={{ my: 0.5 }} />

				{/* Items list */}
				<MDBox sx={{ maxHeight: 240, overflowY: "auto", px: 0.75, pb: 0.75 }}>
					{filteredItems.length === 0 ? (
						<MDBox py={1.5} textAlign="center">
							<MDTypography variant="caption" color="secondary">
								{i18n.t("FILTERS.panel.noResults")}
							</MDTypography>
						</MDBox>
					) : (
						filteredItems.map((item, key) => {
							const selected = isSelected(item);
							return (
								<MDBox
									key={item?.value ?? item?.code ?? key}
									onClick={() => toggleItem(item)}
									sx={({ palette, borders }) => ({
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										px: 0.5,
										py: 0.25,
										borderRadius: borders.borderRadius.md,
										cursor: "pointer",
										backgroundColor: selected
											? darkMode
												? palette.grey[800]
												: palette.grey[100]
											: "transparent",
										"&:hover": {
											backgroundColor: darkMode ? palette.grey[700] : palette.grey[200]
										}
									})}
								>
									<Checkbox checked={selected} size="small" sx={{ p: 0.5 }} />
									<MDTypography
										variant="button"
										fontWeight="regular"
										color="text"
										noWrap
										sx={{ display: "block", minWidth: 0 }}
									>
										{item?.label}
									</MDTypography>
								</MDBox>
							);
						})
					)}
				</MDBox>
			</Popover>
		</>
	);
}
